import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Event } from '@casetrace/shared';
import { useCaseEvent } from '../../hooks/use-case-event';

/**
 * Leser ?event= fra URL, henter hendelsen, åpner den i tidslinjen
 * og rydder søkeparametere når scroll er klart.
 */
export function useFocusEventFromSearch(
  caseId: string,
  eventIdFromSearch: string | undefined,
  openEvent: (event: Event) => void,
  scrollCompletedForId: string | null,
) {
  const navigate = useNavigate();
  const openedForId = useRef<string | null>(null);
  const { data: focusEvent } = useCaseEvent(caseId, eventIdFromSearch);

  useEffect(() => {
    if (!eventIdFromSearch || !focusEvent) return;
    if (openedForId.current === eventIdFromSearch) return;
    openedForId.current = eventIdFromSearch;
    openEvent(focusEvent);
  }, [eventIdFromSearch, focusEvent, openEvent]);

  useEffect(() => {
    if (!eventIdFromSearch) {
      openedForId.current = null;
      return;
    }
    if (scrollCompletedForId !== eventIdFromSearch) return;
    void navigate({
      to: '/cases/$caseId',
      params: { caseId },
      search: {},
      replace: true,
    });
  }, [caseId, eventIdFromSearch, navigate, scrollCompletedForId]);

  return { focusEvent: focusEvent ?? null };
}
